import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../stores/authStore';
import { API_BASE_URL } from '../api/client';
import { exportSubscribersToCSV } from '../utils/csvExport';

interface AdminSubscriber {
  id: number;
  email: string;
  subscribedAt: string;
  active: boolean;
}

export default function AdminSubscribersPage() {
  const queryClient = useQueryClient();
  const { token } = useAuthStore();
  const [editingSubscriber, setEditingSubscriber] = useState<number | null>(null);
  const [newStatus, setNewStatus] = useState<boolean>(true);

  const { data: subscribers, isLoading, error } = useQuery<AdminSubscriber[]>({
    queryKey: ['admin-subscribers'],
    queryFn: async () => {
      const response = await fetch(`${API_BASE_URL}/api/admin/subscribers`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error:', response.status, errorText);
        throw new Error(`Failed to fetch subscribers: ${response.status}`);
      }
      return response.json();
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ subscriberId, active }: { subscriberId: number; active: boolean }) => {
      const response = await fetch(`${API_BASE_URL}/api/admin/subscribers/${subscriberId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ active }),
      });
      if (!response.ok) throw new Error('Failed to update status');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-subscribers'] });
      setEditingSubscriber(null);
      setNewStatus(true);
    },
  });

  const deleteSubscriberMutation = useMutation({
    mutationFn: async (subscriberId: number) => {
      const response = await fetch(`${API_BASE_URL}/api/admin/subscribers/${subscriberId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error('Failed to delete subscriber');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-subscribers'] });
    },
  });

  if (isLoading) {
    return (
      <div className="text-white text-center py-20">
        <div className="text-xl">Loading subscribers...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-white">
        <h1 className="text-3xl font-bold" style={{ fontFamily: "'Orbitron', sans-serif" }}>Email Subscribers</h1>
        <div className="mt-6 p-6 bg-red-500/10 border border-red-500/30 rounded-lg">
          <p className="text-red-400 font-semibold">Error loading subscribers</p>
          <p className="text-red-300 text-sm mt-2">{error.message}</p>
          <p className="text-zinc-400 text-sm mt-2">Check browser console for details</p>
        </div>
      </div>
    );
  }

  const handleExportCSV = () => {
    if (subscribers && subscribers.length > 0) {
      exportSubscribersToCSV(subscribers);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: "'Orbitron', sans-serif" }}>Email Subscribers</h1>
          <p className="mt-2 text-zinc-400">Manage email newsletter subscribers</p>
        </div>
        {subscribers && subscribers.length > 0 && (
          <button
            onClick={handleExportCSV}
            className="px-4 py-2 bg-green-500/20 border border-green-500/30 rounded-lg text-green-400 hover:bg-green-500/30 hover:text-green-300 transition-all duration-300 font-['Orbitron'] text-sm font-bold tracking-wider uppercase"
          >
            Export CSV
          </button>
        )}
      </div>

      {!subscribers || subscribers.length === 0 ? (
        <div className="card p-8 text-center">
          <p className="text-zinc-400 text-lg">No subscribers found in the database</p>
        </div>
      ) : (
        <div className="card">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="px-6 py-4 text-left text-sm font-semibold text-white">ID</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-white">Email</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-white">Status</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-white">Subscribed</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-white">Actions</th>
                </tr>
              </thead>
              <tbody>
                {subscribers?.map((subscriber) => (
                  <tr key={subscriber.id} className="border-b border-white/5">
                    <td className="px-6 py-4 text-sm text-zinc-300">{subscriber.id}</td>
                    <td className="px-6 py-4 text-sm text-white font-medium">{subscriber.email}</td>
                    <td className="px-6 py-4 text-sm">
                      {editingSubscriber === subscriber.id ? (
                        <select
                          value={newStatus ? 'true' : 'false'}
                          onChange={(e) => setNewStatus(e.target.value === 'true')}
                          className="input-field text-sm py-1"
                        >
                          <option value="true">Active</option>
                          <option value="false">Inactive</option>
                        </select>
                      ) : (
                        <span
                          className={`px-2 py-1 rounded text-xs font-semibold ${
                            subscriber.active
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-red-500/20 text-red-400'
                          }`}
                        >
                          {subscriber.active ? 'Active' : 'Inactive'}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-zinc-400">
                      {new Date(subscriber.subscribedAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-sm text-right space-x-2">
                      {editingSubscriber === subscriber.id ? (
                        <>
                          <button
                            onClick={() => {
                              updateStatusMutation.mutate({ subscriberId: subscriber.id, active: newStatus });
                            }}
                            className="text-green-400 hover:text-green-300 font-medium"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => {
                              setEditingSubscriber(null);
                              setNewStatus(true);
                            }}
                            className="text-zinc-400 hover:text-zinc-300 font-medium"
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => {
                              setEditingSubscriber(subscriber.id);
                              setNewStatus(subscriber.active);
                            }}
                            className="text-cyan-400 hover:text-cyan-300 font-medium"
                          >
                            Edit Status
                          </button>
                          <button
                            onClick={() => {
                              if (window.confirm(`Are you sure you want to delete subscriber ${subscriber.email}?`)) {
                                deleteSubscriberMutation.mutate(subscriber.id);
                              }
                            }}
                            className="text-red-400 hover:text-red-300 font-medium"
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
